import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';
import { SECOND } from 'time-constants';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useShowNextLDLProduct } from '@hooks';
import { currentLocationWeatherState } from '@store';

import { currentWeatherConditionsType } from '../../../constants/types/api/currentWeatherConditionsType';
import { getWeatherPhrase } from '../../../helpers/getWeatherPhrase';
import { TWCAnimatedIcon } from '../../primitives/TWCAnimatedIcon/TWCAnimatedIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { LDL } from '../styles';

const tPath = 'lowerDisplayLine.now';
const testIDPath = lowerDisplayLineTestIDs.now;
const displayDurationPerPage = 5 * SECOND;

const LDLNowAPI = () => {
  const { data } = useRecoilValue(currentLocationWeatherState);
  const showNextProduct = useShowNextLDLProduct();

  useEffect(() => {
    !data && showNextProduct(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data) {
    return <></>;
  }

  return <LDLNowUI data={data} onFinish={showNextProduct} />;
};

const LDLNowUI = ({
  data: {
    iconCodeExtend,
    temperature,
    temperatureFeelsLike,
    relativeHumidity,
    windDirectionCardinal,
    windGust,
    windSpeed,
  },
  onFinish,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });
  const slideAnimation = useRef(new Animated.Value(0)).current;

  const animations = [
    Animated.timing(slideAnimation, {
      delay: displayDurationPerPage,
      toValue: -LDL.height,
      duration: 500,
      useNativeDriver: true,
    }),
    Animated.timing(slideAnimation, {
      delay: displayDurationPerPage,
      toValue: -LDL.height,
      useNativeDriver: true,
    }),
  ];

  useEffect(() => {
    const animation = Animated.sequence(animations);

    animation.start(({ finished }) => finished && onFinish?.());
    return animation.reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weatherPhrase = getWeatherPhrase({ iconCodeExtend });

  return (
    <>
      <TWCText style={styles.productText} testID={testIDPath.productLabel} variant={'condensedText36Transparent'}>
        {t('productLabel')}
      </TWCText>
      <TWCAnimatedIcon code={iconCodeExtend} type={'current'} />
      <TWCText style={styles.itemText} testID={testIDPath.temperature} variant={'condensedText36'}>
        {temperature ? `${temperature}°` : ''}
      </TWCText>
      <TWCText style={styles.itemText} testID={testIDPath.weatherPhrase} variant={'condensedText36'}>
        {weatherPhrase ? weatherPhrase : ''}
      </TWCText>
      <View style={styles.slideContainer}>
        <Animated.View
          style={[
            styles.animatedWrapper,
            {
              transform: [{ translateY: slideAnimation }],
            },
          ]}
        >
          <View style={styles.pageWrapper}>
            <TWCText style={styles.itemText} testID={testIDPath.feelsLike} variant={'condensedText36'}>
              {temperatureFeelsLike ? `${t('feelsLikeText')} ${temperatureFeelsLike}°` : `${t('feelsLikeText')}`}
            </TWCText>
            <TWCText style={styles.itemText} testID={testIDPath.humidity} variant={'condensedText36'}>
              {relativeHumidity ? `${t('humidityText')} ${relativeHumidity}%` : `${t('humidityText')}`}
            </TWCText>
          </View>
          <View style={styles.pageWrapper}>
            <View style={styles.windRow}>
              {windDirectionCardinal ? (
                <TWCText
                  style={[styles.itemText, styles.windDirectionCardinalText]}
                  testID={testIDPath.windDirectionCardinal}
                  variant={'condensedText36'}
                >
                  {`${windDirectionCardinal} `}
                </TWCText>
              ) : null}
              <TWCText
                style={[styles.itemText, styles.windSpeedText]}
                testID={testIDPath.windSpeed}
                variant={'condensedText36'}
              >
                {windSpeed ? `${windSpeed} mph` : t('noWindSpeedText')}
              </TWCText>
            </View>
            <TWCText style={styles.itemText} testID={testIDPath.gusts} variant={'condensedText36'}>
              {windGust ? t('gustsText', { windGust }) : t('noGustsText')}
            </TWCText>
          </View>
        </Animated.View>
      </View>
    </>
  );
};

export const LDLNow = {
  API: LDLNowAPI,
  UI: LDLNowUI,
};

LDLNowUI.propTypes = {
  data: currentWeatherConditionsType,
  onFinish: PropTypes.func,
};

LDLNowUI.defaultProps = {
  data: null,
  onFinish: () => {},
};

const styles = StyleSheet.create({
  animatedWrapper: {
    position: 'absolute',
  },
  itemText: {
    paddingLeft: Theme.px(18),
    paddingRight: Theme.px(42),
  },
  pageWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    height: LDL.height,
  },
  productText: {
    paddingRight: Theme.px(48),
  },
  slideContainer: {
    flexGrow: 1,
    height: LDL.height,
  },
  windDirectionCardinalText: {
    paddingRight: 0,
  },
  windRow: {
    flexDirection: 'row',
  },
  windSpeedText: {
    paddingLeft: 0,
  },
});
